/**
 * Agrégats de progression (lot F) — PURS, testés.
 */

import {
  currentPackIndex,
  packMasteryPercent,
  sortPacks,
  subjectLabel,
} from '@jeraisonne/db/curriculum';
import type { TimelinePack } from '@jeraisonne/ui';
import type { MyProgressionPack, TimelinePackRow } from './api';

export interface YearSummary {
  mastered: number;
  total: number;
  hora: number;
  percent: number | null;
  current: MyProgressionPack | null;
}

/** Résumé de l'année d'un élève (une matière, ou toutes si `subject` absent). */
export function yearSummary(
  packs: readonly MyProgressionPack[],
  subject?: string | null,
  today = new Date(),
): YearSummary {
  const list = sortPacks(packs.filter((p) => !subject || p.subject === subject));
  // Une carte peut figurer dans plusieurs packs (révisions) : on somme par pack,
  // c'est un indicateur de parcours, pas un compte de cartes distinctes.
  const mastered = list.reduce((s, p) => s + p.mastered_count, 0);
  const total = list.reduce((s, p) => s + p.card_count, 0);
  const hora = list.reduce((s, p) => s + p.hora, 0);
  const idx = currentPackIndex(list, today);
  return {
    mastered,
    total,
    hora,
    percent: packMasteryPercent(mastered, total),
    current: idx >= 0 ? (list[idx] ?? null) : null,
  };
}

/** Matières présentes, dans l'ordre d'apparition (pour les onglets). */
export function progressionSubjects(
  packs: readonly { subject: string }[],
): { value: string; label: string }[] {
  return [...new Set(packs.map((p) => p.subject))].map((value) => ({
    value,
    label: subjectLabel(value),
  }));
}

export function toTimelineFromProgression(p: MyProgressionPack): TimelinePack {
  return {
    id: p.id,
    title: p.title,
    hook: p.hook,
    startMonth: p.start_month,
    endMonth: p.end_month,
    color: p.color,
    icon: p.icon,
    cardCount: p.card_count,
    progress: packMasteryPercent(p.mastered_count, p.card_count),
    hora: p.hora,
  };
}

export function toTimelineFromRow(p: TimelinePackRow): TimelinePack {
  return {
    id: p.id,
    title: p.title,
    hook: p.hook,
    startMonth: p.start_month,
    endMonth: p.end_month,
    color: p.color,
    icon: p.icon,
    cardCount: p.cards.length,
  };
}
